import type {NotificationInternal} from '../../../client/internal';

import type {
  NotificationOptions,
  Notification,
  TemplateNames,
} from '../../../client';

/**
 * Typeguard to check and cast a `Notification` to a specific Notification Template type.
 *
 * @tutorial
 * ```
 * const template: NotificationOptions = {
 *  template: 'markdown',
 *  body: '',
 *  //...
 * }
 * if(!isTemplate<TemplateMarkdown>(template, 'markdown')){
 * // We know the template is not markdown
 * }
 * if(isTemplate<TemplateMarkdown>(template, 'markdown')){
 *  // Now the template is guarenteed to be TemplateMarkdown
 *  console.log(template.body);
 * }
 * ```
 */
export const isTemplate = <Desired extends NotificationOptionsTypes>(
  options: { template?: TemplateNameType },
  template: GetTemplateName<Desired['template']>
): options is RequireTemplate<Desired> => options.template === template;
type TemplateNameType = keyof typeof TemplateNames;
type NotificationOptionsTypes = NotificationOptions | Notification | NotificationInternal;
type RequireTemplate<T extends NotificationOptionsTypes> = Omit<T, 'template'> & {
  template: GetTemplateName<T['template']>;
};
export type GetTemplateName<
  T extends NotificationOptionsTypes['template']
> = T extends TemplateNameType ? TemplateNameType : 'markdown';
